import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { BOOKING_PROCESS_NAME, FREE_BOOKING_PROCESS_NAME, INQUIRY_PROCESS_NAME, STUDENT_OFFER_NAME, getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { DEFAULT_BOOKING, DEFAULT_FREE_BOOKING, DEFAULT_INQUIRY, INQUIRE_TRANSITION, INQUIRY, INQUIRY_STUDENT, INQUIRY_TUTOR, REQUEST_STUDENT, REQUEST_TRANSITION, REQUEST_TUTOR } from '../../util/constant';
import { STUDENT, TUTOR } from '../../util/types';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

export const FETCH_MESSAGE_REQUEST = 'app/InboxPage/FETCH_MESSAGE_REQUEST';
export const FETCH_MESSAGE_SUCCESSS = 'app/InboxPage/FETCH_MESSAGE_SUCCESSS';
export const FETCH_MESSAGE_ERROR = 'app/InboxPage/FETCH_MESSAGE_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
  messageRequest: false,
  messages: [],
  messageError: null,
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };
    case FETCH_MESSAGE_REQUEST:
      return { ...state, messageRequest: true };
    case FETCH_MESSAGE_SUCCESSS:
      return { ...state, messageRequest: false, messages: payload, messageError: null };
    case FETCH_MESSAGE_ERROR:
      return { ...state, messageRequest: false, messages: [], messageError: payload.error };
    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

const fetchMessageRequest = () => ({ type: FETCH_MESSAGE_REQUEST });
const fetchMessageSuccess = (data) => ({ type: FETCH_MESSAGE_SUCCESSS, payload: data });
const fetchMessageError = (err) => ({ type: FETCH_MESSAGE_ERROR, payload: { err } });

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;

export const fetchTransactionMessages = txIds => (dispatch, getState, sdk) => {
  dispatch(fetchMessageRequest());
  const messageData = txIds.map(id => sdk.messages.query({
    transactionId: id
  }).then(response =>{
    const messgaes = response.data.data;
    const hasMessages = messgaes.length;
    return hasMessages ? ({ txId: id, messageAt: messgaes[0]?.attributes?.createdAt }) : ({}); 
  }).catch(err =>{
    console.log(err, "FETCHING MESSAGES ERROR");
    return dispatch(fetchMessageError(err));
  }));
  
  return Promise.all(messageData).then(results => dispatch(fetchMessageSuccess(results)));
};

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const inquiryTabs = [INQUIRY_STUDENT, INQUIRY_TUTOR];
  const requestTabs = [REQUEST_STUDENT, REQUEST_TUTOR];

  const releventTab = () => {
    switch(tab){
      case INQUIRY_STUDENT:
        return onlyFilterValues.orders;
      case INQUIRY_TUTOR:
        return onlyFilterValues.sales;
      case REQUEST_STUDENT:
        return onlyFilterValues.sales;
      case REQUEST_TUTOR:
        return onlyFilterValues.orders;
      default:
        return onlyFilterValues[tab];
    }
  };

  const onlyFilter = releventTab();

  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  const { page = 1 } = parse(search);

  const apiQueryParams = {
    only: onlyFilter,
    lastTransitions: inquiryTabs.includes(tab) ? [INQUIRE_TRANSITION] : requestTabs.includes(tab) ? [REQUEST_TRANSITION] : getAllTransitionsForEveryProcess(),
    processNames: [BOOKING_PROCESS_NAME, FREE_BOOKING_PROCESS_NAME, INQUIRY_PROCESS_NAME, STUDENT_OFFER_NAME],
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
      'messages'
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData'
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType', 'publicData.timeZone'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const inboxItems = response.data.data;
      const hasItems = (Array.isArray(inboxItems) && inboxItems.length) || false;
      const transactiondIds = (hasItems && inboxItems.map(item => item.id.uuid)) || [];
      hasItems && dispatch(fetchTransactionMessages(transactiondIds));

      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};